import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useAuthToken } from '../TokenContext';
import { Box, Typography, Avatar, Chip, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RateReviewIcon from '@mui/icons-material/RateReview';

const TIME_INTERVALS = [
  { label: 'year', seconds: 31536000 },
  { label: 'month', seconds: 2592000 },
  { label: 'day', seconds: 86400 },
  { label: 'hour', seconds: 3600 },
  { label: 'minute', seconds: 60 },
  { label: 'second', seconds: 1 },
];

/* ── Styled Components ─────────────────────────────────────────────── */

const PageWrap = styled(Box)(() => ({
  width: '100%',
  maxWidth: 800,
  margin: '0 auto',
  padding: '4px',
}));

const HeaderCard = styled(Box)(() => ({
  marginBottom: 24,
  borderBottom: '1px solid var(--artemy-border)',
  paddingBottom: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
}));

const ReviewCard = styled(Box)(() => ({
  background: 'var(--artemy-bg-card)',
  border: '1px solid var(--artemy-border)',
  borderRadius: 14,
  padding: '20px 24px',
  marginBottom: 14,
  transition: 'border-color 0.2s, transform 0.2s',
  '&:hover': {
    borderColor: 'var(--artemy-border-bright)',
    transform: 'translateY(-1px)',
  },
}));

const EmptyState = styled(Box)(() => ({
  background: 'var(--artemy-bg-card)',
  border: '1px solid var(--artemy-border)',
  borderRadius: 14,
  padding: '48px 24px',
  textAlign: 'center',
}));

const StarRow = ({ rating }) => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    stars.push(
      i < rating
        ? <StarIcon key={i} sx={{ fontSize: 16, color: 'var(--artemy-yellow)' }} />
        : <StarBorderIcon key={i} sx={{ fontSize: 16, color: 'var(--artemy-text-dim)' }} />
    );
  }
  return <Box sx={{ display: 'flex', gap: 0.2 }}>{stars}</Box>;
};

/* ── Component ─────────────────────────────────────────────────────── */

const ReviewsAndRatingClient = () => {
  const { user } = useSelector(state => state.auth);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = useAuthToken();

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.post(
          process.env.REACT_APP_CLIENT_GETCLIENTREVIEW,
          { clientID: user.data[0].emp_id },
          { headers: { Authorization: token, 'Content-Type': 'application/json' } }
        );
        const data = response.data.data;
        setReviews(data === 'There Are No Reviews.' ? [] : (data || []));
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [token, user.data]);

  const timeSince = (date) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    const interval = TIME_INTERVALS.find(i => i.seconds < seconds) || TIME_INTERVALS[TIME_INTERVALS.length - 1];
    const count = Math.floor(seconds / interval.seconds);
    return `${count} ${interval.label}${count !== 1 ? 's' : ''} ago`;
  };

  return (
    <PageWrap>
      {/* Header */}
      <HeaderCard>
        <Box sx={{
          width: 38, height: 38, borderRadius: '10px',
          background: 'rgba(61,127,255,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px solid rgba(61,127,255,0.2)',
        }}>
          <RateReviewIcon sx={{ color: 'var(--artemy-accent)', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: 'var(--artemy-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--artemy-text-primary)', letterSpacing: '-0.4px' }}>
            Pod Feedback
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'var(--artemy-text-secondary)', mt: 0.2 }}>
            Reviews and ratings received for your pods
          </Typography>
        </Box>
        {!loading && (
          <Chip
            label={`${reviews.length} review${reviews.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              ml: 'auto',
              background: 'rgba(61,127,255,0.1)',
              color: 'var(--artemy-accent)',
              border: '1px solid rgba(61,127,255,0.2)',
              fontFamily: 'var(--artemy-font-mono)',
              fontSize: 11,
              height: 24,
            }}
          />
        )}
      </HeaderCard>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress size={36} sx={{ color: 'var(--artemy-accent)' }} />
        </Box>
      ) : reviews.length > 0 ? (
        reviews.map((review, index) => (
          <ReviewCard key={index} className="artemy-fade-up" style={{ animationDelay: `${index * 0.05}s` }}>
            {/* Review Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <Avatar
                src={review.profilePicture}
                sx={{
                  width: 40, height: 40,
                  background: `hsl(${(review.podName?.charCodeAt(0) || 0) * 10}, 55%, 45%)`,
                  fontSize: 15, fontWeight: 600,
                  border: '1px solid var(--artemy-border)',
                }}
              >
                {review.podName?.charAt(0) || '?'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--artemy-text-primary)', fontFamily: 'var(--artemy-font-display)' }}>
                    {review.podName}
                  </Typography>
                  <StarRow rating={review.rating} />
                  <Chip
                    label={`${review.rating}/5`}
                    size="small"
                    sx={{
                      height: 20,
                      background: 'rgba(245,166,35,0.12)',
                      color: 'var(--artemy-yellow)',
                      border: '1px solid rgba(245,166,35,0.25)',
                      fontFamily: 'var(--artemy-font-mono)',
                      fontSize: 10,
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 11, color: 'var(--artemy-text-dim)', mt: 0.3, fontFamily: 'var(--artemy-font-mono)' }}>
                  {review.createdAt ? timeSince(review.createdAt) : ''}
                </Typography>
              </Box>
            </Box>

            {/* Review Body */}
            <Box sx={{
              background: 'var(--artemy-bg-elevated)',
              border: '1px solid var(--artemy-border)',
              borderRadius: 8,
              padding: '12px 14px',
            }}>
              <Typography sx={{ fontSize: 13, color: 'var(--artemy-text-secondary)', lineHeight: 1.7, fontStyle: 'italic' }}>
                "{review.review}"
              </Typography>
            </Box>
          </ReviewCard>
        ))
      ) : (
        <EmptyState>
          <RateReviewIcon sx={{ fontSize: 48, color: 'var(--artemy-text-dim)', mb: 2 }} />
          <Typography sx={{ fontSize: 15, color: 'var(--artemy-text-secondary)', fontWeight: 600 }}>
            No Reviews Yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'var(--artemy-text-dim)', mt: 0.5 }}>
            Reviews from your pods will appear here
          </Typography>
        </EmptyState>
      )}
    </PageWrap>
  );
};

export default ReviewsAndRatingClient;